import { useTranslation } from 'react-i18next';
import { Card } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Bed, Bath, Maximize, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Property } from '@/types';
import { getResponsiveImageProps } from '@/types';

interface PropertyCardProps {
  property: Property;
  /** Card index in grid - used to set fetchpriority for above-the-fold cards */
  index?: number;
}

export default function PropertyCard({ property, index = 0 }: PropertyCardProps) {
  const { t, i18n } = useTranslation();
  // First 6 cards are likely above the fold - prioritize their images
  const isAboveFold = index < 6;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-SA' : 'en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <Card className="group overflow-hidden bg-white border-0 shadow-sm hover:shadow-xl transition-all duration-500">
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          {...getResponsiveImageProps(property.photos, 'card')}
          alt={property.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          // @ts-expect-error - fetchpriority is valid HTML but not in React types yet
          fetchpriority={isAboveFold ? 'high' : 'auto'}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <Badge className="absolute top-4 right-4 bg-white/95 text-slate-800 font-medium px-3 py-1">
          {property.propertyType.label}
        </Badge>
        <div className="absolute bottom-4 right-4 left-4">
          <p className="text-white text-2xl font-bold tracking-tight">
            {formatPrice(property.price)}
          </p>
        </div>
      </div>

      <div className="p-5">
        <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-1">
          {property.title}
        </h3>

        <div className="flex items-center gap-1.5 text-slate-500 mb-4">
          <MapPin className="w-4 h-4" />
          <span className="text-sm">
            {property.district?.label ? `${property.district.label}، ` : ''}
            {property.city.label}
          </span>
        </div>

        <div className="flex items-center gap-4 text-slate-600 mb-5 pb-5 border-b border-slate-100">
          <div className="flex items-center gap-1.5">
            <Bed className="w-4 h-4" />
            <span className="text-sm font-medium">{property.bedrooms}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Bath className="w-4 h-4" />
            <span className="text-sm font-medium">{property.bathrooms}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Maximize className="w-4 h-4" />
            <span className="text-sm font-medium">{property.area} {t('properties.sqm')}</span>
          </div>
        </div>

        <Link to={`/properties/${property.id}`}>
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium h-11">
            {t('common.viewDetails')}
          </Button>
        </Link>
      </div>
    </Card>
  );
}
